import { useState, type ReactNode } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Icon,
  Modal,
  Notice,
  PageHeader,
  Panel,
  Tabs,
  Toggle,
} from "../components/ui";
import { useDemo } from "../lib/demo";
import { type DemoRole } from "../lib/model";
import { api, ApiError } from "../../lib/api.ts";
import type { BillingResponse, Disposition, ManualPage, ReportsResponse } from "../../lib/api.ts";
import { itemsOf, qk, useResource } from "../../lib/useApi.ts";
import { DISPOSITION_LABELS, money } from "../../lib/format.ts";
import { stampDate } from "../lib/time.ts";

export function InsightsPage({ page }: { page: string }) {
  if (page === "reports") return <Reports />;
  if (page === "billing") return <Billing />;
  if (page === "manual") return <Manual />;
  return <DesignSystem />;
}

function Reports() {
  const [range, setRange] = useState<7 | 30>(7);
  const [table, setTable] = useState(false);
  const query = useResource<ReportsResponse>(qk.reports(range), () => api.reports(range), { staleTimeMs: 30_000 });

  if (query.isLoading) {
    return (
      <div className="page-status" role="status" aria-live="polite">
        <Icon name="loader" className="spin" size={26} />
        <p>Loading reports…</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Panel title="Reports unavailable">
        <p className="muted">{(query.error as ApiError | null)?.detail || "The console could not reach NightDesk."}</p>
        <Button variant="primary" loading={query.isFetching} onClick={() => void query.refetch()}>
          <Icon name="refresh" /> Try again
        </Button>
      </Panel>
    );
  }

  const r = query.data;
  const t = r.totals;
  const data = r.daily;

  return (
    <>
      <PageHeader
        eyebrow="Understand"
        title="The night, in perspective"
        description="A quieter morning starts with knowing what happened."
        actions={<Badge tone="blue">{r.rangeDays} nights</Badge>}
      />
      <Tabs
        value={String(range)}
        onChange={(v) => setRange(Number(v) as 7 | 30)}
        items={[
          { value: "7", label: "7 nights" },
          { value: "30", label: "30 nights" },
        ]}
      />
      <div className="stat-strip report-stats">
        <div className="stat">
          <strong>{t.calls}</strong>
          <span>Calls in this period</span>
        </div>
        <div className="stat">
          <strong>{t.deflectedPct}%</strong>
          <span>Deflected without a page</span>
        </div>
        <div className="stat">
          <strong>{t.escalations}</strong>
          <span>Escalations</span>
        </div>
        <div className="stat">
          <strong>{money(t.savingsCents)}</strong>
          <span>Estimated savings</span>
        </div>
      </div>
      <Panel
        title="Calls after closing"
        action={
          <Button variant="ghost" onClick={() => setTable(!table)}>
            {table ? "Show chart" : "Show data table"}
          </Button>
        }
      >
        {table ? (
          <div className="table-scroll">
            <table className="data-table">
              <caption className="sr-only">Calls, deflected, and escalations per night</caption>
              <thead>
                <tr>
                  <th>Night</th>
                  <th>Calls</th>
                  <th>Deflected</th>
                  <th>Escalations</th>
                </tr>
              </thead>
              <tbody>
                {data.map((d) => (
                  <tr key={d.date}>
                    <td>
                      {d.date}
                      {d.storm && <small>Storm night</small>}
                    </td>
                    <td>{d.calls}</td>
                    <td>{d.deflected}</td>
                    <td>{d.escalations}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : data.length ? (
          <div
            className="chart-wrap"
            role="img"
            aria-label={`${t.calls} calls and ${t.escalations} escalations over ${r.rangeDays} nights. Use Show data table for individual values.`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 18, right: 18, bottom: 4, left: -18 }}>
                <defs>
                  <linearGradient id="call-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4c88f8" stopOpacity={0.32} />
                    <stop offset="100%" stopColor="#4c88f8" stopOpacity={0.015} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="var(--line)" strokeDasharray="4 4" />
                <XAxis dataKey="date" tick={{ fill: "var(--muted)", fontSize: 12 }} tickLine={false} axisLine={false} minTickGap={35} />
                <YAxis allowDecimals={false} tick={{ fill: "var(--muted)", fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, borderColor: "var(--line)", background: "var(--surface)", color: "var(--ink)" }} />
                <Area type="monotone" dataKey="calls" name="Calls" stroke="#2969e8" strokeWidth={3} fill="url(#call-fill)" isAnimationActive={false} />
                <Area type="monotone" dataKey="escalations" name="Escalations" stroke="#a87616" strokeWidth={2} fill="transparent" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState icon="chart" title="No nights in range" text="Once NightDesk answers calls, they appear here." />
        )}
        <div className="chart-legend">
          <span>
            <i style={{ background: "#2969e8" }} />
            Calls
          </span>
          <span>
            <i style={{ background: "#a87616" }} />
            Escalations
          </span>
        </div>
      </Panel>
      <div className="grid-two margin-top">
        <Panel title="Outcomes">
          <div className="panel-body stack">
            {r.dispositions.length ? (
              r.dispositions.map((d) => (
                <div key={d.disposition}>
                  <div className="section-line">
                    <Link to={`/calls?outcome=${d.disposition}`}>{DISPOSITION_LABELS[d.disposition]}</Link>
                    <strong>{d.count}</strong>
                  </div>
                  <div className="usage-track" aria-hidden="true">
                    <span style={{ width: `${(d.count / Math.max(t.calls, 1)) * 100}%` }} />
                  </div>
                </div>
              ))
            ) : (
              <p className="muted">No calls in range.</p>
            )}
          </div>
        </Panel>
        <Panel title="Top reasons callers rang">
          <div className="panel-body stack">
            {r.topIntents.length ? (
              r.topIntents.map((i) => (
                <div key={i.intent} className="section-line">
                  <span>{i.intent}</span>
                  <strong>{i.count}</strong>
                </div>
              ))
            ) : (
              <p className="muted">Not enough data yet.</p>
            )}
            <hr />
            <div className="section-line">
              <span>Median answer (no tool)</span>
              <strong>{r.latency.p50NoToolMs === null ? "—" : `${r.latency.p50NoToolMs} ms`}</strong>
            </div>
            <div className="section-line">
              <span>Median answer (tool-bound)</span>
              <strong>{r.latency.p50ToolMs === null ? "—" : `${r.latency.p50ToolMs} ms`}</strong>
            </div>
          </div>
        </Panel>
      </div>
    </>
  );
}
function Billing() {
  const query = useResource<BillingResponse>(qk.billing, () => api.billing(), { staleTimeMs: 60_000 });

  if (query.isLoading) {
    return (
      <div className="page-status" role="status" aria-live="polite">
        <Icon name="loader" className="spin" size={26} />
        <p>Loading billing…</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Panel title="Billing unavailable">
        <p className="muted">{(query.error as ApiError | null)?.detail || "The console could not reach NightDesk."}</p>
        <Button variant="primary" loading={query.isFetching} onClick={() => void query.refetch()}>
          <Icon name="refresh" /> Try again
        </Button>
      </Panel>
    );
  }

  const b = query.data;
  const u = b.usage;
  const usedPct = u.minutesIncluded > 0 ? Math.min(100, (u.minutesUsed / u.minutesIncluded) * 100) : 0;

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Billing"
        description="The plan, usage, and history in one place."
        actions={<Badge tone={b.status === "active" ? "green" : "amber"}>{b.status}</Badge>}
      />
      <div className="grid-two">
        <Panel title="Your plan">
          <div className="panel-body">
            {b.plan ? (
              <>
                <Badge tone="blue">{b.plan.name}</Badge>
                <div className="plan-price">
                  {money(b.plan.baseCents)}
                  <span> / month</span>
                </div>
                <p className="muted">
                  {b.plan.includedMinutes.toLocaleString()} minutes included, then {money(b.plan.overagePerMinuteCents)}/min.
                </p>
                <hr />
                <div className="section-line">
                  <span>Projected this period</span>
                  <strong>{money(u.projectedTotalCents)}</strong>
                </div>
                {(b.periodStart || b.periodEnd) && (
                  <p className="muted">
                    {b.periodStart ? stampDate(b.periodStart) : "—"} – {b.periodEnd ? stampDate(b.periodEnd) : "—"}
                  </p>
                )}
              </>
            ) : (
              <EmptyState icon="billing" title="No plan on file" text="This workspace has no billing plan configured yet." />
            )}
          </div>
        </Panel>
        <Panel title="Usage this period">
          <div className="panel-body">
            <div className="section-line">
              <h3>Voice minutes</h3>
            </div>
            <div className="plan-price">
              {u.minutesUsed.toLocaleString()}
              <span> / {u.minutesIncluded.toLocaleString()} included</span>
            </div>
            <div className="usage-track" aria-label={`${u.minutesUsed} of ${u.minutesIncluded} minutes`}>
              <span style={{ width: `${usedPct}%` }} />
            </div>
            <hr />
            <dl className="detail-list">
              <div>
                <dt>Overage</dt>
                <dd>
                  {u.overageMinutes.toLocaleString()} min · {money(u.overageCents)}
                </dd>
              </div>
              <div>
                <dt>Storm minutes</dt>
                <dd>
                  {u.stormMinutes.toLocaleString()} min · {money(u.stormCents)}
                </dd>
              </div>
              <div>
                <dt>SMS</dt>
                <dd>
                  {u.smsUsed.toLocaleString()} / {u.smsIncluded.toLocaleString()} included
                </dd>
              </div>
            </dl>
          </div>
        </Panel>
      </div>
      <Panel title="Invoice history" className="margin-top">
        {b.invoices.length ? (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>
                    <span className="sr-only">Open invoice</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {b.invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <strong>{inv.number || inv.id.slice(0, 8)}</strong>
                    </td>
                    <td>
                      {stampDate(inv.periodStart)} – {stampDate(inv.periodEnd)}
                    </td>
                    <td>{money(inv.totalCents)}</td>
                    <td>
                      <Badge tone={inv.status === "paid" ? "green" : "amber"}>{inv.status}</Badge>
                    </td>
                    <td>
                      {inv.hostedUrl ? (
                        <a className="button button-ghost" href={inv.hostedUrl} target="_blank" rel="noreferrer">
                          Open <Icon name="external" size={14} />
                        </a>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="billing" title="No invoices yet" text="Invoices appear here at the end of each billing period." />
        )}
      </Panel>
    </>
  );
}
/**
 * Manual body arrives as light markdown. It is turned into React elements here — never
 * dangerouslySetInnerHTML — so markup in a body can't execute. Supports what the chapters use:
 * #/##/### headings, - and 1. lists, paragraphs (wrapped lines joined), **bold**, `code`, and
 * [links](...). It used to print every block as plain text, literal asterisks and all.
 */
function inlineMd(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${keyBase}-${n++}`;
    if (m[1] !== undefined) out.push(<strong key={k}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(<code key={k}>{m[2]}</code>);
    else {
      const href = m[4]!;
      out.push(
        href.startsWith("/") ? (
          <Link key={k} to={href}>{m[3]}</Link>
        ) : /^https?:\/\//.test(href) ? (
          <a key={k} href={href} target="_blank" rel="noreferrer noopener">{m[3]}</a>
        ) : (
          m[3]
        ),
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function ManualBody({ body }: { body: string }) {
  const lines = body.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flushPara = () => {
    if (para.length) {
      const k = `p${blocks.length}`;
      blocks.push(<p key={k}>{inlineMd(para.join(" "), k)}</p>);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const k = `l${blocks.length}`;
      const items = list.items.map((t, i) => <li key={i}>{inlineMd(t, `${k}-${i}`)}</li>);
      blocks.push(list.ordered ? <ol key={k}>{items}</ol> : <ul key={k}>{items}</ul>);
      list = null;
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const numbered = /^\d+\.\s+(.*)$/.exec(line);
    if (!line) {
      flushPara();
      flushList();
    } else if (heading) {
      flushPara();
      flushList();
      const k = `h${blocks.length}`;
      const level = heading[1]!.length;
      const content = inlineMd(heading[2]!, k);
      blocks.push(level === 1 ? <h2 key={k}>{content}</h2> : level === 2 ? <h3 key={k}>{content}</h3> : <h4 key={k}>{content}</h4>);
    } else if (bullet || numbered) {
      flushPara();
      const ordered = Boolean(numbered);
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { ordered, items: [] };
      list.items.push((bullet ?? numbered)![1]!);
    } else if (list && raw.startsWith(" ")) {
      // A wrapped continuation of the previous list item.
      list.items[list.items.length - 1] += ` ${line}`;
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return <>{blocks}</>;
}

function Manual() {
  const { slug } = useParams();
  const [query, setQuery] = useState("");
  const listQ = useResource(qk.manual, () => api.manual(), { staleTimeMs: 5 * 60_000 });

  const pages = itemsOf(listQ.data).slice().sort((a: ManualPage, b: ManualPage) => a.chapter - b.chapter);
  const current = pages.find((p) => p.slug === slug) ?? pages[0];
  const matches = query
    ? pages.filter((p) => `${p.title} ${p.body}`.toLowerCase().includes(query.trim().toLowerCase()))
    : pages;

  return (
    <>
      <PageHeader
        eyebrow="A little guidance"
        title="NightDesk manual"
        description="Straight answers for the person keeping things running."
      />
      <div className="toolbar">
        <div className="search-field">
          <Icon name="search" />
          <input
            aria-label="Search manual"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the manual"
          />
        </div>
      </div>
      {listQ.isLoading ? (
        <div className="page-status" role="status" aria-live="polite">
          <Icon name="loader" className="spin" size={26} />
          <p>Loading the manual…</p>
        </div>
      ) : listQ.isError ? (
        <Panel>
          <EmptyState
            icon="file"
            title="Could not load the manual"
            action={
              <Button variant="primary" onClick={() => void listQ.refetch()}>
                Try again
              </Button>
            }
          />
        </Panel>
      ) : query ? (
        <Panel>
          {matches.length ? (
            matches.map((c) => (
              <Link key={c.slug} to={`/manual/${c.slug}`} className="list-row" onClick={() => setQuery("")}>
                <Icon name="file" />
                <span>
                  <strong>{c.title}</strong>
                  <small>Chapter {c.chapter}</small>
                </span>
                <Icon name="arrow" />
              </Link>
            ))
          ) : (
            <EmptyState title="No matching chapters" text="Try a shorter search." />
          )}
        </Panel>
      ) : (
        <div className="manual-layout">
          <nav className="manual-nav" aria-label="Manual chapters">
            {pages.map((c) => (
              <NavLink key={c.slug} className={c.slug === current?.slug ? "active" : ""} to={`/manual/${c.slug}`}>
                {c.title}
              </NavLink>
            ))}
          </nav>
          {current ? (
            <Panel>
              <article className="panel-body manual-copy">
                <h2>{current.title}</h2>
                {/* Chapters open with their own "# Title"; the page already shows it above. */}
                <ManualBody body={current.body.replace(/^\s*#\s+[^\n]*\n/, "")} />
              </article>
            </Panel>
          ) : (
            <EmptyState title="The manual is empty" icon="file" />
          )}
        </div>
      )}
    </>
  );
}
function DesignSystem() {
  const { role, setRole, reset, pending } = useDemo();
  const [checked, setChecked] = useState(true);
  const [modal, setModal] = useState(false);
  return (
    <>
      <PageHeader
        eyebrow="Developer handoff"
        title="Open Sky"
        description="A calm control surface. One obvious active service. Everything else in its place."
        actions={<Badge tone="blue">Design system · v1</Badge>}
      />
      <Panel title="Preview controls">
        <div className="panel-body grid-two">
          <div>
            <Field
              label="Preview role"
              hint="Local UI behavior only. This is not authentication."
            >
              <select
                value={role}
                disabled={pending !== null}
                onChange={(e) => setRole(e.target.value as DemoRole)}
              >
                <option value="owner">Owner</option>
                <option value="operator">Operator</option>
                <option value="viewer">Viewer</option>
              </select>
            </Field>
            <Button icon="refresh" disabled={pending !== null} onClick={reset}>
              Reset sample data
            </Button>
          </div>
          <div>
            <h3>Boundary states</h3>
            <div className="button-row margin-top">
              {["loading", "empty", "error", "denied"].map((state) => (
                <Link key={state} className="button" to={`/?preview=${state}`}>
                  {state.charAt(0).toUpperCase() + state.slice(1)}
                </Link>
              ))}
            </div>
            <p className="muted margin-top">
              Each is a static design fixture. Reloading also resets business
              data; only your appearance preference persists.
            </p>
          </div>
        </div>
      </Panel>
      <Panel title="Color language" className="margin-top">
        <div className="panel-body token-grid">
          {[
            ["Canvas", "--bg"],
            ["Surface", "--surface"],
            ["Ink", "--ink"],
            ["Muted", "--muted"],
            ["Primary", "--primary"],
            ["Border", "--line"],
          ].map(([name, token]) => (
            <div className="token" key={token}>
              <div
                className="token-swatch"
                style={{ background: `var(${token})` }}
              />
              <strong>{name}</strong>
              <code>{token}</code>
            </div>
          ))}
        </div>
      </Panel>
      <div className="grid-two margin-top">
        <Panel title="Controls">
          <div className="panel-body stack">
            <div className="button-row">
              <Button variant="primary" onClick={() => setModal(true)}>
                Primary action
              </Button>
              <Button onClick={() => setModal(true)}>Secondary</Button>
              <Button disabled>Unavailable</Button>
            </div>
            <div className="button-row">
              <Badge tone="green" dot>
                Active
              </Badge>
              <Badge tone="amber" dot>
                Needs attention
              </Badge>
              <Badge tone="red" dot>
                Action required
              </Badge>
              <Badge>Unknown</Badge>
            </div>
            <Toggle
              checked={checked}
              onChange={setChecked}
              label="Example switch"
              description="State has both a label and a visible control."
            />
            <Field
              label="Example input"
              hint="Keep helper copy specific and close to its control."
            >
              <input placeholder="Type something" />
            </Field>
          </div>
        </Panel>
        <Panel title="The composition">
          <div className="panel-body">
            <ol className="design-principles">
              <li>
                <strong>Service first.</strong> The blue-wave hero is the
                identity and state anchor.
              </li>
              <li>
                <strong>Activation second.</strong> Pinned services are large,
                direct, and touch-friendly.
              </li>
              <li>
                <strong>Attention, not noise.</strong> Escalations and exact
                caller wording sit one level below.
              </li>
              <li>
                <strong>Details on demand.</strong> Reports, configuration, and
                audit evidence have their own screens.
              </li>
            </ol>
            <p className="muted">
              Responsive styles target phone, tablet, and desktop.
              Browser/device acceptance testing is still required before
              production.
            </p>
          </div>
        </Panel>
      </div>
      <Notice>
        This is a working frontend reference, not a connected phone system.
        Start with CLAUDE.md in the source package before wiring services,
        authentication, providers, or billing.
      </Notice>
      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title="A focused decision"
        description="One task, clear context, and an obvious way back."
        footer={
          <>
            <Button onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => setModal(false)}>
              Done
            </Button>
          </>
        }
      >
        <p>
          Dialogs are centered on a desktop and become bottom sheets on a phone.
          Escape closes them; focus returns to the control that opened them.
        </p>
      </Modal>
    </>
  );
}
